/**
 * ClientMonitorDashboardPage — Monitor, the client product surface (Sprint 49).
 *
 * Route: /product/monitor (founder-gated for tonight's MVP so the Founder can
 * SEE the buyer-facing product over the same real data). This is the "Signed
 * Verdict" view a Monitor *buyer/operator* reads — NOT the OMEGA founder
 * cockpit. It consumes the existing, unchanged endpoints:
 *   - GET /api/v1/admin/monitor/verdicts  (recent signed verdicts)
 *   - GET /api/v1/monitor/verify/{id}     (public, no-auth tamper check)
 *
 * Production client access is per-tenant-scoped — a known follow-up. The page
 * states that honestly in a banner so the surface is not mistaken for shipped
 * multi-tenant auth.
 *
 * White, client-grade theme. Loading / empty / error states are all guarded.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

// ── Wire types (mirror crates/gi-api-server/src/routes/monitor.rs) ────────────

interface VerdictView {
  verdict_id: string;
  sequence: number;
  canonical_json: string;
  signature: string;
  signer_pubkey: string;
  signer_fingerprint: string;
  severity: string;
  created_iso: string;
}

interface VerdictsListResponse {
  verdicts: VerdictView[];
  total: number;
}

interface VerifyResponse extends VerdictView {
  /** Server re-ran the Ed25519 check over the stored canonical bytes. */
  valid: boolean;
}

// ── Formatters ────────────────────────────────────────────────────────────────

const SEVERITY_STYLE: Record<string, { fg: string; bg: string; border: string; label: string }> = {
  ok: { fg: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', label: 'OK' },
  warning: { fg: '#b45309', bg: '#fffbeb', border: '#fde68a', label: 'Warning' },
  critical: { fg: '#b91c1c', bg: '#fef2f2', border: '#fecaca', label: 'Critical' },
};

function severityStyle(s: string) {
  return SEVERITY_STYLE[s] ?? { fg: '#374151', bg: '#f9fafb', border: '#e5e7eb', label: s };
}

function fmtTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortId(id: string): string {
  return id.length > 16 ? `${id.slice(0, 12)}…${id.slice(-4)}` : id;
}

// ── Palette (white, client-grade) ─────────────────────────────────────────────

const ink = '#111827';
const inkMuted = '#4b5563';
const inkSubtle = '#6b7280';
const rim = '#e5e7eb';
const accent = '#1d4ed8';

// ── Verify affordance ─────────────────────────────────────────────────────────

function VerifyButton({ verdictId }: { verdictId: string }) {
  const [result, setResult] = useState<'verified' | 'tampered' | null>(null);
  const m = useMutation({
    mutationFn: () => apiFetch<VerifyResponse>(`/monitor/verify/${verdictId}`),
    onSuccess: (r) => setResult(r.valid ? 'verified' : 'tampered'),
  });

  if (result === 'verified') {
    return (
      <span style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>✓ Verified</span>
    );
  }
  if (result === 'tampered') {
    return <span style={{ fontSize: 12, fontWeight: 600, color: '#b91c1c' }}>✗ Tampered</span>;
  }
  return (
    <button
      type="button"
      onClick={() => m.mutate()}
      disabled={m.isPending}
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: accent,
        background: '#ffffff',
        border: `1px solid ${rim}`,
        borderRadius: 6,
        padding: '11px 10px',
        cursor: m.isPending ? 'default' : 'pointer',
      }}
    >
      {m.isPending ? 'Verifying…' : m.isError ? 'Retry verify' : 'Verify'}
    </button>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ClientMonitorDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const verdicts = useQuery({
    queryKey: ['client', 'monitor', 'verdicts'],
    queryFn: () => apiFetch<VerdictsListResponse>('/admin/monitor/verdicts'),
    refetchInterval: 30_000,
  });

  const rows = verdicts.data?.verdicts ?? [];
  const total = verdicts.data?.total ?? 0;
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches;

  return (
    <div
      style={{
        maxWidth: 960,
        margin: '0 auto',
        padding: '28px 24px 48px',
        color: ink,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      }}
    >
      {/* Tenant-auth honesty banner */}
      <div
        style={{
          fontSize: 12,
          color: inkSubtle,
          background: '#f9fafb',
          border: `1px solid ${rim}`,
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 20,
        }}
      >
        Preview over live data. Production client access is per-tenant scoped — that authentication
        is a known follow-up; this view currently renders under the operator shell.
      </div>

      {/* ── Hero: The Signed Verdict ── */}
      <header style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: accent,
            }}
          >
            Monitor
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#15803d',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 999,
              padding: '2px 10px',
            }}
          >
            Deterministic · Ed25519-signed
          </span>
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 10px', lineHeight: 1.15 }}>
          The Signed Verdict
        </h1>
        <p style={{ fontSize: 16, color: inkMuted, margin: 0, maxWidth: 680, lineHeight: 1.5 }}>
          Every other monitor tells you what happened. Monitor proves what happened — and, with your
          approval, fixes it and signs the receipt.
        </p>
      </header>

      {/* ── Recent signed verdicts ── */}
      <section style={{ marginBottom: 36 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Recent signed verdicts</h2>
          <span style={{ fontSize: 12, color: inkSubtle }}>
            {total} total · showing {rows.length}
          </span>
        </div>

        {verdicts.isPending && (
          <p style={{ fontSize: 13, color: inkSubtle }}>Loading verdicts…</p>
        )}
        {verdicts.isError && (
          <p style={{ fontSize: 13, color: '#b91c1c' }}>
            Could not load verdicts. The signing service may be starting up — retrying automatically.
          </p>
        )}
        {!verdicts.isPending && !verdicts.isError && rows.length === 0 && (
          <div
            style={{
              border: `1px dashed ${rim}`,
              borderRadius: 10,
              padding: '28px 20px',
              textAlign: 'center',
              color: inkSubtle,
              fontSize: 13,
            }}
          >
            No verdicts yet. As soon as telemetry is ingested, each detection is signed and listed
            here — every row independently verifiable.
          </div>
        )}

        {rows.length > 0 && (
          <div
            style={{
              border: `1px solid ${rim}`,
              borderRadius: 10,
              overflow: 'hidden',
              background: '#ffffff',
            }}
          >
            {rows.map((v, i) => {
              const sev = severityStyle(v.severity);
              return (
                <div
                  key={v.verdict_id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? 'minmax(0,1fr) auto auto'
                      : 'minmax(0,1.4fr) auto minmax(0,1fr) auto',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderTop: i === 0 ? 'none' : `1px solid ${rim}`,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        color: ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={v.verdict_id}
                    >
                      {shortId(v.verdict_id)}
                    </div>
                    <div style={{ fontSize: 11, color: inkSubtle, marginTop: 2 }}>
                      seq #{v.sequence} · {fmtTime(v.created_iso)}
                    </div>
                  </div>

                  <span
                    style={{
                      justifySelf: 'start',
                      fontSize: 11,
                      fontWeight: 700,
                      color: sev.fg,
                      background: sev.bg,
                      border: `1px solid ${sev.border}`,
                      borderRadius: 999,
                      padding: '2px 10px',
                    }}
                  >
                    {sev.label}
                  </span>

                  {!isMobile && (
                    <div
                      style={{
                        fontSize: 11,
                        color: inkSubtle,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={v.signer_pubkey}
                    >
                      signer {v.signer_fingerprint}
                    </div>
                  )}

                  <div style={{ justifySelf: 'end' }}>
                    <VerifyButton verdictId={v.verdict_id} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── How this is different ── */}
      <section>
        <h2 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 12px' }}>How this is different</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 14,
          }}
        >
          {[
            {
              title: 'Forecastable bill',
              body: 'Deterministic detection means same inputs → same verdict. No usage-metered, compounding bill shock — you can forecast the spend.',
            },
            {
              title: 'Governed remediation',
              body: 'Detect → tier-approve (T1/T2/T3) → execute → verify → signed verdict, in one attributable loop. The authorization is proven, not just the action.',
            },
            {
              title: 'Determinism + proof',
              body: 'Every verdict is Ed25519-signed, hash-chained, and publicly verifiable offline. A single flipped byte makes verification fail — that is the un-doctored proof.',
            },
          ].map((c) => (
            <div
              key={c.title}
              style={{
                border: `1px solid ${rim}`,
                borderRadius: 10,
                padding: '16px 18px',
                background: '#ffffff',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{c.title}</div>
              <p style={{ fontSize: 13, color: inkMuted, margin: 0, lineHeight: 1.5 }}>{c.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default ClientMonitorDashboardPage;
